/**
 * Erreurs API côté client.
 *
 * Le backend renvoie un `code` STABLE et un `detail` en anglais destiné aux
 * développeurs. On n'affiche jamais `detail` à l'utilisateur : on traduit le
 * `code` (docs/22 §5).
 */

/** Réponse RFC 9457 telle que produite par l'API (docs/08 §6). */
export interface ProblemDetails {
  type?: string;
  title?: string;
  status: number;
  detail?: string;
  code: string;
  instance?: string;
  request_id?: string;
  errors?: { field: string; code: string; message: string }[];
  /** Présent sur un 402 : la fonctionnalité à débloquer. */
  feature?: string;
  upgrade_url?: string;
  /** Présent sur un 402 : l'offre d'où l'on vient, pour un paywall qui sait quoi proposer. */
  current_plan?: string;
  /**
   * Présents sur `quota_exceeded` et `limit_reached` (docs/10 §3).
   *
   * Le serveur les envoie **ensemble** : « 5 000 Mo maximum » ne dit pas à
   * l'utilisateur s'il lui reste dix mégaoctets ou zéro, et c'est la seule
   * chose qui l'aide à choisir entre effacer et payer.
   */
  limit?: number;
  used?: number;
  requested?: number;
  current?: number;
}

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly requestId: string | undefined;
  readonly fieldErrors: { field: string; code: string; message: string }[];
  readonly feature: string | undefined;
  readonly currentPlan: string | undefined;
  readonly limit: number | undefined;
  readonly used: number | undefined;
  readonly current: number | undefined;

  constructor(problem: ProblemDetails) {
    // `message` sert au diagnostic (logs, Sentry), jamais à l'affichage.
    super(problem.detail ?? problem.code);
    this.name = "ApiError";
    this.code = problem.code;
    this.status = problem.status;
    this.requestId = problem.request_id;
    this.fieldErrors = problem.errors ?? [];
    this.feature = problem.feature;
    this.currentPlan = problem.current_plan;
    this.limit = problem.limit;
    this.used = problem.used;
    this.current = problem.current;
  }

  /**
   * Un 402 signifie « offre insuffisante » : l'UI montre un paywall, pas un refus.
   *
   * Testé sur le **statut** et non sur le code : `entitlement_required`,
   * `quota_exceeded` et `limit_reached` sont trois 402 qui appellent trois
   * messages différents mais le même geste — proposer la mise à niveau. Une
   * liste de codes à tenir à jour ici raterait le quatrième.
   */
  get requiresUpgrade(): boolean {
    return this.status === 402;
  }
}

function isProblemDetails(value: unknown): value is ProblemDetails {
  return (
    typeof value === "object" && value !== null && "code" in value && typeof value.code === "string"
  );
}

/**
 * Normalise une erreur `openapi-fetch` en `ApiError`.
 *
 * Une panne réseau ne produit aucun corps : on fabrique alors un code
 * `network_error`, traduit comme les autres.
 */
export function toApiError(error: unknown, status?: number): ApiError {
  if (isProblemDetails(error)) {
    return new ApiError(error);
  }
  return new ApiError({
    code: status === undefined ? "network_error" : "unexpected_error",
    status: status ?? 0,
    detail: error instanceof Error ? error.message : undefined,
  });
}
